/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient, baseClient } from '@store/index';
import { axiosErrorMiddleware } from '../common';
import { QueryFunctionContext } from '@tanstack/react-query';
import {
  CreateBucketParams,
  DeleteAllBucketObjectsParams,
  DeleteBucketObjectsParams,
  DeleteBucketParams,
  DesyncedBucket,
  DisableBucketEmbeddingsParams,
  EmbeddingTask,
  GetBucketFileParams,
  GetBucketFilesParams,
  GetBucketParams,
  GetBucketsParams,
  GetDesyncedBucketsParams,
  GetTaskParams,
  SearchBucketParams,
  SyncBucketParams,
  TelnyxBucket,
  TelnyxBucketFile,
  TelnyxBucketFileContent,
  TelnyxBucketFileEmbedding,
} from './buckets.types';

export const getBucketFile = async ({ queryKey }: QueryFunctionContext<[string, GetBucketFileParams]>) => {
  const [, { service_id, bucket_name, file_name, region }] = queryKey;

  const url = `/${service_id}/buckets/${bucket_name}/${region}/objects/${file_name}`;
  const response = await apiClient.get<TelnyxBucketFileContent>(url);
  return response.data;
};

export const getBuckets = async ({ queryKey }: QueryFunctionContext<[string, string, GetBucketsParams]>) => {
  const [, , { service_id, all = false }] = queryKey;

  const url = `/${service_id}/buckets?all=${all}`;
  const response = await apiClient.get<TelnyxBucket[]>(url);
  return response.data;
};

export const getBucket = async ({ queryKey }: QueryFunctionContext<[string, string, GetBucketParams]>) => {
  const [, , { service_id, bucket_name }] = queryKey;

  const url = `/${service_id}/buckets/${bucket_name}/us-central-1`;
  const response = await apiClient.get<TelnyxBucket>(url);
  return response.data;
};

export const getDesyncedBuckets = async ({
  queryKey,
}: QueryFunctionContext<[string, string, GetDesyncedBucketsParams]>) => {
  const [, , { service_id }] = queryKey;

  const url = `/${service_id}/buckets/desynced`;
  const response = await apiClient.get<DesyncedBucket[]>(url);
  return response.data;
};

export const getBucketFiles = async ({ queryKey }: QueryFunctionContext<[string, string, GetBucketFilesParams]>) => {
  const [, , { service_id, bucket_name, region }] = queryKey;

  const url = `/${service_id}/buckets/${bucket_name}/${region}/objects`;
  const response = await apiClient.get<TelnyxBucketFile[]>(url);
  return response.data;
};

export const getEmbeddedFiles = async ({ queryKey }: QueryFunctionContext<[string, string, GetBucketParams]>) => {
  const [, , { service_id, bucket_name }] = queryKey;

  const url = `/${service_id}/buckets/${bucket_name}/us-central-1/embeddings`;
  const response = await apiClient.get<TelnyxBucketFileEmbedding[]>(url);
  return response.data;
};

export const getTask = async ({ queryKey }: QueryFunctionContext<[string, string, GetTaskParams]>) => {
  const [, , { task_id, service_id }] = queryKey;

  const url = `/${service_id}/buckets/task/${task_id}`;
  const response = await apiClient.get<EmbeddingTask>(url);
  return response.data;
};

export const createBucket = async ({ name, service_id, user_id, region }: CreateBucketParams) => {
  try {
    const url = `/${service_id}/buckets/${name}/${region}`;
    const response = await apiClient.put(url, { user_id });
    if (response?.data) return response?.data;
    return { success: false };
  } catch (error) {
    return axiosErrorMiddleware(error);
  }
};

export const syncBuckets = async ({ buckets, service_id, user_id }: SyncBucketParams) => {
  try {
    const url = `/${service_id}/buckets/sync`;
    const response = await apiClient.post(url, { user_id, buckets });
    if (response?.data) return { ...response?.data, buckets };
    return [];
  } catch (error) {
    return axiosErrorMiddleware(error);
  }
};

export const deleteBucket = async ({ service_id, bucket_name, region }: DeleteBucketParams) => {
  try {
    if (!bucket_name || !region) return;

    const url = `/${service_id}/buckets/${bucket_name}/${region}`;
    const response = await apiClient.delete(url);

    if (response?.data) return { ...response?.data, bucket_name };
    return [];
  } catch (e) {
    return axiosErrorMiddleware(e);
  }
};

export const deleteAllObjectsFromBucket = async ({ service_id, bucket_name, region }: DeleteAllBucketObjectsParams) => {
  try {
    if (!bucket_name || !region || !service_id) return;

    const url = `/${service_id}/buckets/${bucket_name}/${region}/clear`;
    const response = await apiClient.delete(url);

    if (response?.data) return { ...response?.data, bucket_name };
    return { error: 'An unexpected error occured' };
  } catch (error) {
    return axiosErrorMiddleware(error);
  }
};

export const enableBucketEmbeddings = async ({ service_id, bucketName, model, loader }: any) => {
  try {
    if (!bucketName) return;

    const url = `/${service_id}/buckets/${bucketName}/us-central-1/embeddings`;
    const response = await apiClient.post(url, { embedding_model: model, loader });
    if (response?.data) return { ...response?.data, bucket_name: bucketName };

    return { error: 'An unexpected error occured' };
  } catch (error) {
    return axiosErrorMiddleware(error);
  }
};

export const disableBucketEmbeddings = async ({ bucketName, service_id }: DisableBucketEmbeddingsParams) => {
  try {
    if (!bucketName) return;

    const url = `/${service_id}/buckets/${bucketName}/us-central-1/embeddings`;
    const response = await apiClient.delete(url);

    if (response?.data) return { ...response?.data, bucket_name: bucketName };
    return { error: 'An unexpected error occured' };
  } catch (error) {
    return axiosErrorMiddleware(error);
  }
};

export const deleteObjectsFromBucket = async ({
  service_id,
  bucketName,
  region,
  objectNames,
}: DeleteBucketObjectsParams) => {
  try {
    if (!bucketName || !region || !objectNames?.length) return;

    const url = `/${service_id}/buckets/${bucketName}/${region}/objects`;
    const response = await apiClient.delete(url, { data: { objectNames } });
    if (response?.data) return { ...response?.data, bucketName };
    return { error: 'An unexpected error occured' };
  } catch (error) {
    return axiosErrorMiddleware(error);
  }
};

export const queryBucket = async (data: SearchBucketParams) => {
  try {
    if (data.max_results > 20) return { error: 'Max results cannot be greater than 20' };

    const body = { ...data, min_certainty: data.min_certainty / 100 };
    const response = await baseClient.post(LOCAL ? `/ext/noc_chatbot/query` : `/ext/chatbot/query`, body);

    if (response?.data) return response?.data;
    return [];
  } catch (error) {
    return axiosErrorMiddleware(error);
  }
};
